// src/features/customers/components/VersandSichtbarkeitPanel.tsx
//
// Reiter „Versand und Sichtbarkeit" in den Kundenstammdaten.
//
// Führt zusammen, was bisher verstreut war: welcher Ansprechpartner welches
// Paket erhält, wer interne Mitarbeiter sehen darf, und die Freigabe der
// einzelnen Mitarbeiter.
//
// Sicherheitsprinzip: Ein Lohnschein geht nur dann an einen Ansprechpartner
// ohne Vollzugriff, wenn der Mitarbeiter ausdrücklich als extern bestätigt ist
// und sich seine Stammdaten seither nicht geändert haben. Jeder andere Fall
// geht an einen Kontakt mit Vollzugriff. Diese Regel wird nicht hier, sondern
// im Versand-Worker durchgesetzt; das Panel macht sie nur sichtbar und
// bedienbar.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Package, FileText, QrCode, ShieldCheck, ShieldAlert, Users, Search,
  Check, X, AlertTriangle, Lock, Unlock, RefreshCw, Info, Receipt, Activity
} from 'lucide-react';
import {
  CustomerContact,
  MitarbeiterSichtbarkeitZeile,
  MitarbeiterSichtbarkeitKlasse,
  MitarbeiterSichtbarkeitStatus
} from '../../../shared/types';
import { customerContactService } from '../../../services/customer/CustomerContactService';
import { mitarbeiterSichtbarkeitService } from '../../../services/customer/MitarbeiterSichtbarkeitService';
import { supabase } from '../../../shared/services/supabase';
import { useAuth } from '../../auth';

interface VersandSichtbarkeitPanelProps {
  customerId: string;
  beraternummer: string | null;
  mandantennummer: string | null;
  onUpdate: () => void;
}

type StatusFilter = 'offen' | 'alle' | 'extern' | 'intern' | 'verfallen';

const STATUS_TEXT: Record<MitarbeiterSichtbarkeitStatus, string> = {
  offen: 'Offen',
  verfallen: 'Verfallen',
  freigegeben_extern: 'Extern',
  freigegeben_intern: 'Intern'
};

const STATUS_STIL: Record<MitarbeiterSichtbarkeitStatus, { flaeche: string; schrift: string; rahmen: string }> = {
  offen: { flaeche: '#F3F4F6', schrift: '#4B5563', rahmen: '#D1D5DB' },
  verfallen: { flaeche: '#F5F3FF', schrift: '#5B21B6', rahmen: '#DDD6FE' },
  freigegeben_extern: { flaeche: '#ECFDF5', schrift: '#065F46', rahmen: '#6EE7B7' },
  freigegeben_intern: { flaeche: '#EFF6FF', schrift: '#1E40AF', rahmen: '#BFDBFE' }
};

export const VersandSichtbarkeitPanel: React.FC<VersandSichtbarkeitPanelProps> = ({
  customerId,
  beraternummer,
  mandantennummer,
  onUpdate
}) => {
  const { user } = useAuth();

  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [zeilen, setZeilen] = useState<MitarbeiterSichtbarkeitZeile[]>([]);
  const [schalter, setSchalter] = useState(false);
  const [isOppmCustomer, setIsOppmCustomer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [speichertKontakt, setSpeichertKontakt] = useState<string | null>(null);
  const [speichertSchalter, setSpeichertSchalter] = useState(false);
  const [speichertFreigabe, setSpeichertFreigabe] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const [filter, setFilter] = useState<StatusFilter>('offen');
  const [suche, setSuche] = useState('');
  const [bestaetigeUebernahme, setBestaetigeUebernahme] = useState(false);
  const [bestaetigeSchalter, setBestaetigeSchalter] = useState(false);

  const bearbeiter = user?.email || 'unbekannt';

  // ============================================
  // LADEN
  // ============================================

  const ladeAlles = useCallback(async () => {
    setLoading(true);
    setFehler(null);

    const geladeneKontakte = await customerContactService.getAllContactsForCustomer(customerId);
    setContacts(geladeneKontakte);

    const aktiv = await mitarbeiterSichtbarkeitService.getSchalter(customerId);
    setSchalter(aktiv);

    // OPPM-Gruppenzugehörigkeit: entscheidet, ob die beiden QR-Spalten
    // überhaupt angezeigt werden. Getrennte Abfragen, kein PostgREST-Embed.
    try {
      const { data: kunde } = await supabase
        .from('customers')
        .select('customer_group_id')
        .eq('id', customerId)
        .maybeSingle();
      if (kunde?.customer_group_id) {
        const { data: gruppe } = await supabase
          .from('customer_groups')
          .select('name')
          .eq('id', kunde.customer_group_id)
          .maybeSingle();
        // Exakt derselbe Vergleich wie im ContactsManager, damit beide Ansichten
        // dieselbe Gruppenzuordnung treffen.
        setIsOppmCustomer(gruppe?.name === 'OPPM');
      } else {
        setIsOppmCustomer(false);
      }
    } catch (err) {
      console.warn('[VersandSichtbarkeit] Gruppenermittlung fehlgeschlagen (non-blocking):', err);
      setIsOppmCustomer(false);
    }

    if (beraternummer && mandantennummer) {
      // Aktive plus alle mit Dokument in den letzten Abrechnungsperioden.
      // Ausgetretene mit laufender Abrechnung muessen zuordenbar sein, sonst
      // blockieren sie den Versand, ohne in der Liste zu erscheinen.
      const geladeneZeilen = await mitarbeiterSichtbarkeitService.getZeilenMitVersandbedarf(
        beraternummer, mandantennummer, customerId
      );
      setZeilen(geladeneZeilen);
    } else {
      setZeilen([]);
    }

    setLoading(false);
  }, [customerId, beraternummer, mandantennummer]);

  useEffect(() => {
    ladeAlles();
  }, [ladeAlles]);

  // ============================================
  // ABGELEITETE WERTE
  // ============================================

  const aktiveKontakte = useMemo(() => contacts.filter(c => c.is_active), [contacts]);
  const sammelKontakte = useMemo(
    () => aktiveKontakte.filter(c => c.receives_lohnschein_report === true),
    [aktiveKontakte]
  );
  const vollzugriffKontakte = useMemo(
    () => sammelKontakte.filter(c => c.lohnschein_vollzugriff === true),
    [sammelKontakte]
  );

  const zaehler = useMemo(() => {
    const z = { offen: 0, verfallen: 0, extern: 0, intern: 0 };
    for (const zeile of zeilen) {
      if (zeile.status === 'offen') z.offen++;
      else if (zeile.status === 'verfallen') z.verfallen++;
      else if (zeile.status === 'freigegeben_extern') z.extern++;
      else z.intern++;
    }
    return z;
  }, [zeilen]);

  const gefiltert = useMemo(() => {
    const suchbegriff = suche.trim().toLowerCase();
    return zeilen.filter(z => {
      if (filter === 'offen' && z.status !== 'offen' && z.status !== 'verfallen') return false;
      if (filter === 'extern' && z.status !== 'freigegeben_extern') return false;
      if (filter === 'intern' && z.status !== 'freigegeben_intern') return false;
      if (filter === 'verfallen' && z.status !== 'verfallen') return false;
      if (!suchbegriff) return true;
      const heuhaufen = [
        z.nachname ?? '', z.vorname ?? '', z.personalnummer,
        z.betriebliche_personalnummer ?? '', z.kostenstelle ?? '', z.taetigkeit ?? ''
      ].join(' ').toLowerCase();
      return heuhaufen.includes(suchbegriff);
    });
  }, [zeilen, filter, suche]);

  const uebernehmbar = useMemo(
    () => zeilen.filter(z => z.status === 'offen' && z.vorschlag !== null),
    [zeilen]
  );

  /**
   * ZUTEILUNGSVORSCHAU: Wer bekommt beim Sammelpaket welche Mitarbeiter?
   *
   * Die Regel steht im Versandkern und war in der Oberfläche bisher nirgends
   * ablesbar. Ohne sie ist nicht erkennbar, dass ein zusätzlicher Kontakt ohne
   * Vollzugriff die Verteilung grundlegend ändert: Solange es keinen gibt,
   * bekommt der Vollzugriffs-Kontakt alles; sobald einer existiert, bekommt er
   * nur noch die Restmenge.
   *
   * Bewusst eine Vorschau und keine zweite Wahrheit: gerechnet wird dieselbe
   * Regel, entschieden wird weiterhin im Versandkern.
   */
  const zuteilungsVorschau = useMemo(() => {
    if (!schalter || sammelKontakte.length === 0) return null;
    const eingeschraenkt = sammelKontakte.filter(c => c.lohnschein_vollzugriff !== true);
    const vollzugriff = sammelKontakte.filter(c => c.lohnschein_vollzugriff === true);
    const restmenge = zaehler.intern + zaehler.offen + zaehler.verfallen;

    return {
      eingeschraenkt: eingeschraenkt.map(c => ({
        email: c.email,
        anzahl: zaehler.extern,
        was: 'nur bestätigt externe Mitarbeiter'
      })),
      vollzugriff: vollzugriff.map(c => ({
        email: c.email,
        // Ohne eingeschränkten Kontakt bekommt der Vollzugriff alles, damit die
        // Externen nicht stillschweigend verschwinden.
        anzahl: eingeschraenkt.length > 0 ? restmenge : zaehler.extern + restmenge,
        was: eingeschraenkt.length > 0
          ? 'interne, nicht zugeordnete und verfallene'
          : 'alle, weil kein eingeschränkter Empfänger vorhanden ist'
      }))
    };
  }, [schalter, sammelKontakte, zaehler]);

  // Bereitschaftsprüfung: Was fehlt noch, damit der Schalter sinnvoll ist?
  const bereitschaft = useMemo(() => {
    const maengel: string[] = [];
    if (sammelKontakte.length === 0) {
      maengel.push('Kein aktiver Ansprechpartner mit dem Schalter „Lohnschein-Sammelpaket".');
    }
    if (sammelKontakte.length > 0 && vollzugriffKontakte.length === 0) {
      maengel.push('Kein Ansprechpartner mit Vollzugriff. Interne und noch nicht zugeordnete Mitarbeiter hätten keinen Empfänger, der Versand würde eskalieren.');
    }
    const ohneZugang = sammelKontakte.filter(c => !c.secure_download_token || !c.secure_download_passwort);
    if (ohneZugang.length > 0) {
      maengel.push(`${ohneZugang.length} Empfänger ohne vollständigen Portalzugang (Token oder Passwort fehlt).`);
    }
    if (zaehler.offen > 0) {
      maengel.push(`${zaehler.offen} Mitarbeiter ohne Zuordnung. Deren Lohnscheine gehen an den Vollzugriffs-Kontakt.`);
    }
    if (zaehler.verfallen > 0) {
      maengel.push(`${zaehler.verfallen} verfallene Freigaben. Die Stammdaten haben sich seit der Bestätigung geändert.`);
    }
    return maengel;
  }, [sammelKontakte, vollzugriffKontakte, zaehler]);

  // ============================================
  // AKTIONEN
  // ============================================

  const schalteKontakt = async (
    contact: CustomerContact,
    feld: 'receives_finalization_package' | 'receives_lohnschein_report'
      | 'receives_oppm_payslips_qr' | 'receives_monatsuebersicht_qr' | 'lohnschein_vollzugriff'
      | 'portal_rechnungen' | 'portal_eau' | 'portal_mitarbeiter',
    wert: boolean
  ) => {
    setSpeichertKontakt(contact.id);
    setFehler(null);
    const ergebnis = await customerContactService.updateContact(contact.id, { [feld]: wert });
    if (!ergebnis) {
      setFehler('Die Änderung konnte nicht gespeichert werden.');
    } else {
      setContacts(prev => prev.map(c => (c.id === contact.id ? { ...c, [feld]: wert } : c)));
      onUpdate();
    }
    setSpeichertKontakt(null);
  };

  const schalteSichtbarkeit = async (aktiv: boolean) => {
    setSpeichertSchalter(true);
    setFehler(null);
    const ok = await mitarbeiterSichtbarkeitService.setSchalter(customerId, aktiv);
    if (!ok) {
      setFehler('Der Schalter konnte nicht gespeichert werden.');
    } else {
      setSchalter(aktiv);
      setBestaetigeSchalter(false);
      onUpdate();
    }
    setSpeichertSchalter(false);
  };

  const setzeKlasse = async (zeile: MitarbeiterSichtbarkeitZeile, klasse: MitarbeiterSichtbarkeitKlasse) => {
    setSpeichertFreigabe(true);
    setFehler(null);
    const ok = await mitarbeiterSichtbarkeitService.setKlasse({
      customerId: zeile.customer_id,
      beraternummer: zeile.beraternummer,
      mandantennummer: zeile.mandantennummer,
      personalnummer: zeile.personalnummer,
      klasse,
      betrieblichePersonalnummer: zeile.betriebliche_personalnummer,
      kostenstelle: zeile.kostenstelle,
      taetigkeit: zeile.taetigkeit,
      bestaetigtVon: bearbeiter,
      quelle: 'xtax'
    });
    if (!ok) {
      setFehler('Die Zuordnung konnte nicht gespeichert werden.');
    } else {
      await ladeAlles();
    }
    setSpeichertFreigabe(false);
  };

  const entferneFreigabe = async (zeile: MitarbeiterSichtbarkeitZeile) => {
    setSpeichertFreigabe(true);
    setFehler(null);
    const ok = await mitarbeiterSichtbarkeitService.entferneFreigabe(
      zeile.beraternummer, zeile.mandantennummer, zeile.personalnummer
    );
    if (!ok) {
      setFehler('Die Zuordnung konnte nicht zurückgenommen werden.');
    } else {
      await ladeAlles();
    }
    setSpeichertFreigabe(false);
  };

  const uebernimmVorschlaege = async () => {
    setSpeichertFreigabe(true);
    setFehler(null);
    const ok = await mitarbeiterSichtbarkeitService.setKlasseMehrfach(
      uebernehmbar.map(z => ({
        customerId: z.customer_id,
        beraternummer: z.beraternummer,
        mandantennummer: z.mandantennummer,
        personalnummer: z.personalnummer,
        klasse: z.vorschlag as MitarbeiterSichtbarkeitKlasse,
        betrieblichePersonalnummer: z.betriebliche_personalnummer,
        kostenstelle: z.kostenstelle,
        taetigkeit: z.taetigkeit,
        bestaetigtVon: bearbeiter,
        quelle: 'xtax',
        notiz: 'Massenübernahme des Vorschlags nach Sichtprüfung'
      }))
    );
    if (!ok) {
      setFehler('Die Massenübernahme ist fehlgeschlagen.');
    } else {
      setBestaetigeUebernahme(false);
      await ladeAlles();
    }
    setSpeichertFreigabe(false);
  };

  // ============================================
  // BAUSTEINE
  // ============================================

  const abschnittsTitel = (text: string, symbol: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      {symbol}
      <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#111827', margin: 0 }}>{text}</h3>
    </div>
  );

  const kaestchen = (
    contact: CustomerContact,
    feld: 'receives_finalization_package' | 'receives_lohnschein_report'
      | 'receives_oppm_payslips_qr' | 'receives_monatsuebersicht_qr' | 'lohnschein_vollzugriff'
      | 'portal_rechnungen' | 'portal_eau' | 'portal_mitarbeiter',
    farbe: string
  ) => {
    const wert = contact[feld] === true;
    return (
      <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #F3F4F6' }}>
        <input
          type="checkbox"
          checked={wert}
          disabled={speichertKontakt === contact.id}
          onChange={(e) => schalteKontakt(contact, feld, e.target.checked)}
          style={{ width: '16px', height: '16px', accentColor: farbe, cursor: 'pointer' }}
        />
      </td>
    );
  };

  const kopfZelle = (inhalt: React.ReactNode, breite?: string) => (
    <th style={{
      padding: '8px',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: '600',
      color: '#6B7280',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      borderBottom: '1px solid #E5E7EB',
      width: breite,
      verticalAlign: 'bottom'
    }}>
      {inhalt}
    </th>
  );

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>Lade Versand- und Sichtbarkeitsdaten...</div>;
  }

  return (
    <div>
      {fehler && (
        <div style={{
          marginBottom: '16px', padding: '12px', backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA', borderRadius: '8px',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <AlertTriangle size={16} color="#DC2626" />
          <span style={{ fontSize: '14px', color: '#991B1B' }}>{fehler}</span>
        </div>
      )}

      {/* ============================================ */}
      {/* ABSCHNITT 1: EMPFÄNGERMATRIX                 */}
      {/* ============================================ */}
      <div style={{ marginBottom: '28px' }}>
        {abschnittsTitel('Wer bekommt welches Paket', <Users size={18} color="#3B82F6" />)}

        {aktiveKontakte.length === 0 ? (
          <div style={{
            padding: '16px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
            borderRadius: '8px', fontSize: '13px', color: '#991B1B'
          }}>
            Für diesen Kunden ist kein aktiver Ansprechpartner hinterlegt. Bitte zuerst im Reiter
            „Ansprechpartner" anlegen.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{
                    padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600',
                    color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em',
                    borderBottom: '1px solid #E5E7EB'
                  }}>
                    Ansprechpartner
                  </th>
                  {kopfZelle(<><Package size={13} color="#3B82F6" /><div>Monats&shy;abschluss</div></>, '92px')}
                  {kopfZelle(<><FileText size={13} color="#059669" /><div>Lohnschein-Sammelpaket</div></>, '110px')}
                  {isOppmCustomer && kopfZelle(<><QrCode size={13} color="#7C3AED" /><div>Gehalts&shy;dokumente QR</div></>, '96px')}
                  {isOppmCustomer && kopfZelle(<><QrCode size={13} color="#0D9488" /><div>Monats&shy;übersichten QR</div></>, '96px')}
                  {kopfZelle(<><ShieldCheck size={13} color="#B45309" /><div>Vollzugriff</div></>, '92px')}
                  {kopfZelle(<><Receipt size={13} color="#1E40AF" /><div>Rechnungen im Portal</div></>, '92px')}
                  {kopfZelle(<><Activity size={13} color="#BE123C" /><div>eAU im Portal</div></>, '92px')}
                  {kopfZelle(<><Users size={13} color="#0F766E" /><div>Mitarbeiter im Portal</div></>, '92px')}
                  {kopfZelle(<><Lock size={13} color="#6B7280" /><div>Portal&shy;zugang</div></>, '92px')}
                </tr>
              </thead>
              <tbody>
                {aktiveKontakte.map(contact => {
                  const hatZugang = !!contact.secure_download_token && !!contact.secure_download_passwort;
                  return (
                    <tr key={contact.id}>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #F3F4F6' }}>
                        <div style={{ fontWeight: '600', color: '#111827' }}>
                          {customerContactService.getContactDisplayName(contact)}
                          {contact.rolle && (
                            <span style={{ fontWeight: 'normal', color: '#6B7280' }}> ({contact.rolle})</span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>{contact.email}</div>
                      </td>
                      {kaestchen(contact, 'receives_finalization_package', '#3B82F6')}
                      {kaestchen(contact, 'receives_lohnschein_report', '#059669')}
                      {isOppmCustomer && kaestchen(contact, 'receives_oppm_payslips_qr', '#7C3AED')}
                      {isOppmCustomer && kaestchen(contact, 'receives_monatsuebersicht_qr', '#0D9488')}
                      {kaestchen(contact, 'lohnschein_vollzugriff', '#B45309')}
                      {kaestchen(contact, 'portal_rechnungen', '#1E40AF')}
                      {kaestchen(contact, 'portal_eau', '#BE123C')}
                      {kaestchen(contact, 'portal_mitarbeiter', '#0F766E')}
                      <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #F3F4F6' }}>
                        {hatZugang
                          ? <Unlock size={15} color="#059669" />
                          : <Lock size={15} color="#DC2626" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{
          marginTop: '10px', padding: '10px 12px', backgroundColor: '#FFFBEB',
          border: '1px solid #FDE68A', borderRadius: '6px', display: 'flex', gap: '8px'
        }}>
          <ShieldCheck size={15} color="#B45309" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '12px', color: '#92400E', lineHeight: '1.5' }}>
            <strong>Vollzugriff</strong> bedeutet: Dieser Ansprechpartner darf im Sammelpaket auch
            Lohnscheine interner und noch nicht zugeordneter Mitarbeiter erhalten. Alle übrigen
            Ansprechpartner bekommen ausschließlich Mitarbeiter, die ausdrücklich als extern
            bestätigt sind. Dieselbe Regel gilt für eAU und Mitarbeiter im Kundenportal.
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ABSCHNITT 2: SCHALTER + BEREITSCHAFT         */}
      {/* ============================================ */}
      <div style={{ marginBottom: '28px' }}>
        {abschnittsTitel(
          'Getrennte Zustellung nach Sichtbarkeit',
          schalter ? <ShieldCheck size={18} color="#059669" /> : <ShieldAlert size={18} color="#6B7280" />
        )}

        <div style={{
          padding: '14px',
          backgroundColor: schalter ? '#ECFDF5' : '#F9FAFB',
          border: `1px solid ${schalter ? '#6EE7B7' : '#E5E7EB'}`,
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: schalter ? '#065F46' : '#374151' }}>
                {schalter ? 'Aktiv' : 'Nicht aktiv'}
              </div>
              <div style={{ fontSize: '13px', color: schalter ? '#047857' : '#6B7280', marginTop: '2px', lineHeight: '1.5' }}>
                {schalter
                  ? 'Das Sammelpaket wird je Ansprechpartner unterschiedlich befüllt. Passt eine Zuordnung nicht, wird der Versand angehalten statt ausgeliefert.'
                  : 'Das Sammelpaket geht unverändert vollständig an alle empfangsberechtigten Ansprechpartner.'}
              </div>
            </div>
            {!bestaetigeSchalter ? (
              <button
                onClick={() => setBestaetigeSchalter(true)}
                disabled={speichertSchalter}
                style={{
                  padding: '8px 14px', fontSize: '13px', fontWeight: '600',
                  backgroundColor: schalter ? '#FFFFFF' : '#3B82F6',
                  color: schalter ? '#374151' : '#FFFFFF',
                  border: schalter ? '1px solid #D1D5DB' : 'none',
                  borderRadius: '6px', cursor: 'pointer', flexShrink: 0
                }}
              >
                {schalter ? 'Deaktivieren' : 'Aktivieren'}
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button
                  onClick={() => setBestaetigeSchalter(false)}
                  disabled={speichertSchalter}
                  style={{
                    padding: '8px 12px', fontSize: '13px', fontWeight: '600', backgroundColor: '#F3F4F6',
                    color: '#374151', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer'
                  }}
                >
                  Zurück
                </button>
                <button
                  onClick={() => schalteSichtbarkeit(!schalter)}
                  disabled={speichertSchalter}
                  style={{
                    padding: '8px 12px', fontSize: '13px', fontWeight: '600',
                    backgroundColor: speichertSchalter ? '#9CA3AF' : (schalter ? '#DC2626' : '#059669'),
                    color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer'
                  }}
                >
                  {speichertSchalter ? 'Speichert...' : (schalter ? 'Ja, deaktivieren' : 'Ja, aktivieren')}
                </button>
              </div>
            )}
          </div>

          {zuteilungsVorschau && (
            <div style={{
              marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${schalter ? '#A7F3D0' : '#E5E7EB'}`
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#065F46', marginBottom: '6px' }}>
                Wer bekommt das Sammelpaket
              </div>
              <div style={{ fontSize: '12px', color: '#065F46', lineHeight: '1.7' }}>
                {[...zuteilungsVorschau.eingeschraenkt, ...zuteilungsVorschau.vollzugriff].map((e, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: '600', minWidth: '38px', textAlign: 'right' }}>
                      {e.anzahl}
                    </span>
                    <span>{e.was} an {e.email ?? 'Kontakt ohne E-Mail'}</span>
                  </div>
                ))}
                {[...zuteilungsVorschau.eingeschraenkt, ...zuteilungsVorschau.vollzugriff]
                  .some(e => e.anzahl === 0) && (
                  <div style={{ marginTop: '6px', color: '#6B7280' }}>
                    Empfänger ohne zugeteilte Mitarbeiter bekommen kein Paket und keine Mail.
                  </div>
                )}
              </div>
            </div>
          )}

          {bereitschaft.length > 0 && (
            <div style={{
              marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${schalter ? '#A7F3D0' : '#E5E7EB'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <AlertTriangle size={14} color="#B45309" />
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#92400E' }}>
                  Vor der Aktivierung zu klären
                </span>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#92400E', lineHeight: '1.6' }}>
                {bereitschaft.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* ABSCHNITT 3: MITARBEITER-FREIGABE            */}
      {/* ============================================ */}
      <div>
        {abschnittsTitel('Zuordnung der Mitarbeiter', <ShieldCheck size={18} color="#059669" />)}

        {(!beraternummer || !mandantennummer) ? (
          <div style={{
            padding: '16px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
            borderRadius: '8px', fontSize: '13px', color: '#991B1B'
          }}>
            Ohne Berater- und Mandantennummer lassen sich keine Mitarbeiter zuordnen.
            Bitte zuerst im Reiter „Stammdaten" ergänzen.
          </div>
        ) : (
          <>
            {/* Zählerleiste */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {([
                ['offen', `Offen und verfallen (${zaehler.offen + zaehler.verfallen})`],
                ['extern', `Extern (${zaehler.extern})`],
                ['intern', `Intern (${zaehler.intern})`],
                ['verfallen', `Nur verfallen (${zaehler.verfallen})`],
                ['alle', `Alle (${zeilen.length})`]
              ] as [StatusFilter, string][]).map(([wert, text]) => (
                <button
                  key={wert}
                  onClick={() => setFilter(wert)}
                  style={{
                    padding: '5px 12px', fontSize: '12px', fontWeight: filter === wert ? '600' : '500',
                    backgroundColor: filter === wert ? '#3B82F6' : '#FFFFFF',
                    color: filter === wert ? '#FFFFFF' : '#374151',
                    border: `1px solid ${filter === wert ? '#3B82F6' : '#D1D5DB'}`,
                    borderRadius: '999px', cursor: 'pointer'
                  }}
                >
                  {text}
                </button>
              ))}
            </div>

            {/* Suche und Massenübernahme */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
                <Search size={14} color="#9CA3AF" style={{ position: 'absolute', left: '10px', top: '10px' }} />
                <input
                  type="text"
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                  placeholder="Name, Personalnummer, Kostenstelle oder Tätigkeit"
                  style={{
                    width: '100%', padding: '8px 10px 8px 30px', fontSize: '13px',
                    border: '1px solid #D1D5DB', borderRadius: '6px', boxSizing: 'border-box'
                  }}
                />
              </div>
              <button
                onClick={ladeAlles}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px',
                  fontSize: '13px', fontWeight: '500', backgroundColor: '#FFFFFF',
                  border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', color: '#374151'
                }}
              >
                <RefreshCw size={13} />
                Aktualisieren
              </button>
              {uebernehmbar.length > 0 && (
                bestaetigeUebernahme ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setBestaetigeUebernahme(false)}
                      disabled={speichertFreigabe}
                      style={{
                        padding: '8px 12px', fontSize: '13px', fontWeight: '600', backgroundColor: '#F3F4F6',
                        color: '#374151', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer'
                      }}
                    >
                      Zurück
                    </button>
                    <button
                      onClick={uebernimmVorschlaege}
                      disabled={speichertFreigabe}
                      style={{
                        padding: '8px 12px', fontSize: '13px', fontWeight: '600',
                        backgroundColor: speichertFreigabe ? '#9CA3AF' : '#059669',
                        color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer'
                      }}
                    >
                      {speichertFreigabe ? 'Speichert...' : `Ja, ${uebernehmbar.length} übernehmen`}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setBestaetigeUebernahme(true)}
                    style={{
                      padding: '8px 12px', fontSize: '13px', fontWeight: '600', backgroundColor: '#FFFFFF',
                      color: '#065F46', border: '1px solid #6EE7B7', borderRadius: '6px', cursor: 'pointer'
                    }}
                  >
                    {uebernehmbar.length} Vorschläge übernehmen
                  </button>
                )
              )}
            </div>

            {bestaetigeUebernahme && (
              <div style={{
                marginBottom: '12px', padding: '10px 12px', backgroundColor: '#FFFBEB',
                border: '1px solid #FDE68A', borderRadius: '6px', display: 'flex', gap: '8px'
              }}>
                <Info size={15} color="#B45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                <div style={{ fontSize: '12px', color: '#92400E', lineHeight: '1.5' }}>
                  Die Übernahme bestätigt {uebernehmbar.length} Mitarbeiter als extern, weil eine betriebliche
                  Personalnummer hinterlegt ist. Bitte vorher stichprobenartig prüfen. Die Bestätigung wird
                  unter Deinem Namen gespeichert.
                </div>
              </div>
            )}

            {/* Liste */}
            {gefiltert.length === 0 ? (
              <div style={{
                padding: '24px', textAlign: 'center', backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', color: '#6B7280'
              }}>
                {zeilen.length === 0
                  ? 'Für diesen Mandanten liegen keine Mitarbeiter im DATEV-Spiegel.'
                  : 'Keine Mitarbeiter passen zu Filter und Suche.'}
              </div>
            ) : (
              <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                {gefiltert.map((z, index) => {
                  const stil = STATUS_STIL[z.status];
                  return (
                    <div
                      key={`${z.mandantennummer}-${z.personalnummer}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
                        borderBottom: index < gefiltert.length - 1 ? '1px solid #F3F4F6' : 'none',
                        backgroundColor: z.status === 'verfallen' ? '#FAF9FF' : '#FFFFFF'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                          {(z.nachname ?? '') + (z.vorname ? ', ' + z.vorname : '')} · {z.personalnummer}
                          {!z.ist_aktiv && (
                            <span
                              title={z.austrittsdatum
                                ? `Ausgetreten am ${new Date(z.austrittsdatum + 'T00:00:00').toLocaleDateString('de-DE')}. Wird angezeigt, weil eine Abrechnung der letzten Monate vorliegt.`
                                : 'Ausgetreten. Wird angezeigt, weil eine Abrechnung der letzten Monate vorliegt.'}
                              style={{
                                marginLeft: '6px', fontSize: '10px', fontWeight: '600',
                                padding: '2px 6px', borderRadius: '4px',
                                backgroundColor: '#F3F4F6', color: '#6B7280', whiteSpace: 'nowrap'
                              }}
                            >
                              ausgetreten{z.austrittsdatum ? ` ${new Date(z.austrittsdatum + 'T00:00:00').toLocaleDateString('de-DE')}` : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          {z.taetigkeit || 'ohne Tätigkeit'}
                          {z.kostenstelle ? ` · KSt ${z.kostenstelle}` : ''}
                          {z.betriebliche_personalnummer ? ` · Betr.-Nr. ${z.betriebliche_personalnummer}` : ' · keine betriebliche Personalnummer'}
                        </div>
                        {z.status === 'verfallen' && (
                          <div style={{ fontSize: '12px', color: '#5B21B6', marginTop: '2px' }}>
                            Stammdaten haben sich seit der Bestätigung geändert. Bitte erneut zuordnen.
                          </div>
                        )}
                        {z.bestaetigt_von && z.status !== 'offen' && (
                          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
                            Bestätigt von {z.bestaetigt_von}
                            {z.bestaetigt_am ? ` am ${new Date(z.bestaetigt_am).toLocaleDateString('de-DE')}` : ''}
                            {z.bestaetigt_quelle === 'kunde' ? ' (Angabe des Kunden)' : ''}
                          </div>
                        )}
                      </div>

                      <span style={{
                        flexShrink: 0, padding: '3px 10px', borderRadius: '999px', fontSize: '11px',
                        fontWeight: '600', backgroundColor: stil.flaeche, color: stil.schrift,
                        border: `1px solid ${stil.rahmen}`
                      }}>
                        {STATUS_TEXT[z.status]}
                      </span>

                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
                        {/* VORSCHLAG SICHTBAR MACHEN (11.09.2026)
                            Zuvor war der Extern-Knopf immer grün umrandet und mit Häkchen
                            versehen, unabhängig vom Vorschlag. Das wirkte wie eine
                            Empfehlung. Eine interne Kraft ohne betriebliche Personalnummer
                            sah damit genauso aus wie ein externer Mitarbeiter, und ein
                            unbedachter Klick hätte ihre Gehaltsdokumente ins Kundenportal
                            gegeben. Jetzt wird nur der Knopf hervorgehoben, den die
                            Vorschlagslogik tatsächlich empfiehlt; ohne Vorschlag bleiben
                            beide neutral und erzwingen eine bewusste Entscheidung. */}
                        {z.status === 'offen' && (
                          <span
                            title={z.vorschlag_begruendung}
                            style={{
                              fontSize: '10px', fontWeight: '600', whiteSpace: 'nowrap',
                              padding: '2px 6px', borderRadius: '4px',
                              backgroundColor: z.vorschlag === 'intern' ? '#EFF6FF'
                                             : z.vorschlag === 'extern' ? '#ECFDF5' : '#FEF3C7',
                              color: z.vorschlag === 'intern' ? '#1E40AF'
                                   : z.vorschlag === 'extern' ? '#065F46' : '#92400E'
                            }}
                          >
                            {z.vorschlag === 'intern' ? 'Vorschlag: intern'
                              : z.vorschlag === 'extern' ? 'Vorschlag: extern'
                              : 'Kein Vorschlag'}
                          </span>
                        )}
                        <button
                          onClick={() => setzeKlasse(z, 'extern')}
                          disabled={speichertFreigabe || z.status === 'freigegeben_extern'}
                          title={z.status === 'offen' && z.vorschlag !== 'extern'
                            ? `Als externen Mitarbeiter bestätigen. Achtung: ${z.vorschlag_begruendung}`
                            : 'Als externen Mitarbeiter bestätigen'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px',
                            fontSize: '12px', fontWeight: '600',
                            backgroundColor: z.status === 'freigegeben_extern' ? '#ECFDF5' : '#FFFFFF',
                            color: (z.status === 'freigegeben_extern' || z.vorschlag === 'extern') ? '#065F46' : '#6B7280',
                            border: `1px solid ${(z.status === 'freigegeben_extern' || z.vorschlag === 'extern') ? '#6EE7B7' : '#D1D5DB'}`,
                            borderRadius: '6px',
                            cursor: z.status === 'freigegeben_extern' ? 'default' : 'pointer',
                            opacity: speichertFreigabe ? 0.6 : 1
                          }}
                        >
                          {(z.status === 'freigegeben_extern' || z.vorschlag === 'extern') && <Check size={12} />}
                          Extern
                        </button>
                        <button
                          onClick={() => setzeKlasse(z, 'intern')}
                          disabled={speichertFreigabe || z.status === 'freigegeben_intern'}
                          title={z.status === 'offen' && z.vorschlag !== 'intern'
                            ? `Als internen Mitarbeiter kennzeichnen. Achtung: ${z.vorschlag_begruendung}`
                            : 'Als internen Mitarbeiter kennzeichnen'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px',
                            fontSize: '12px', fontWeight: '600',
                            backgroundColor: z.status === 'freigegeben_intern' ? '#EFF6FF' : '#FFFFFF',
                            color: (z.status === 'freigegeben_intern' || z.vorschlag === 'intern') ? '#1E40AF' : '#6B7280',
                            border: `1px solid ${(z.status === 'freigegeben_intern' || z.vorschlag === 'intern') ? '#BFDBFE' : '#D1D5DB'}`,
                            borderRadius: '6px',
                            cursor: z.status === 'freigegeben_intern' ? 'default' : 'pointer',
                            opacity: speichertFreigabe ? 0.6 : 1
                          }}
                        >
                          {(z.status === 'freigegeben_intern' || z.vorschlag === 'intern') && <ShieldCheck size={12} />}
                          Intern
                        </button>
                        {z.status !== 'offen' && (
                          <button
                            onClick={() => entferneFreigabe(z)}
                            disabled={speichertFreigabe}
                            title="Zuordnung zurücknehmen"
                            style={{
                              display: 'inline-flex', alignItems: 'center', padding: '5px 8px',
                              backgroundColor: '#FFFFFF', border: '1px solid #D1D5DB',
                              borderRadius: '6px', cursor: 'pointer', opacity: speichertFreigabe ? 0.6 : 1
                            }}
                          >
                            <X size={12} color="#6B7280" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: '10px', fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>
              Mitarbeiter ohne Zuordnung sind nicht blockiert. Ihre Lohnscheine gehen an den
              Ansprechpartner mit Vollzugriff, nicht an die übrigen Empfänger.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VersandSichtbarkeitPanel;
